import type { WorkflowEdge, WorkflowGraphDefinition, WorkflowNode, WorkflowResources } from '../../services/workflowsApi.ts';
import { isTerminalPort, quickReplies, workflowPorts } from './workflowGraph.ts';

export interface WorkflowPoint { x: number; y: number }
export interface WorkflowCardSize { width: number; height: number; portY?: Record<string, number> }
type TemplateNode = Extract<WorkflowNode, { type: 'official_template' }>;
type RepliesNode = Extract<WorkflowNode, { type: 'template_replies' }>;
export interface WorkflowMessageGroup { template: TemplateNode; replies: RepliesNode }
export interface WorkflowVisualPort {
  id: string; source: string; source_port: string; label: string; target: string | null;
  terminal: boolean; point: WorkflowPoint; side: 'right' | 'bottom'; edgeId?: string;
}
export interface WorkflowVisualCard {
  id: string; nodeIds: string[]; node: WorkflowNode; replyNode?: RepliesNode;
  kind: 'message' | WorkflowNode['type']; position: WorkflowPoint;
  width: number; height: number; outputs: WorkflowVisualPort[];
}
export type WorkflowCard = WorkflowVisualCard;
export interface WorkflowProjection {
  cards: WorkflowVisualCard[]; hiddenEdgeIds: string[];
  cardByNodeId: Record<string, WorkflowVisualCard>;
}

/** Grouping is a read-only view: an incoherent pair remains two explicit cards. */
export function findMessageGroup(graph: WorkflowGraphDefinition, nodeId: string): WorkflowMessageGroup | null {
  const selected = graph.nodes.filter(n => n.id === nodeId);
  if (selected.length !== 1) return null;
  const node = selected[0];
  const templateId = node.type === 'official_template' ? node.id : node.type === 'template_replies' ? node.config.template_node_id : null;
  const templates = graph.nodes.filter((n): n is TemplateNode => n.id === templateId && n.type === 'official_template');
  if (templates.length !== 1) return null;
  const template = templates[0], outgoing = graph.edges.filter(e => e.source === template.id && e.source_port === 'accepted');
  if (outgoing.length !== 1 || graph.edges.filter(e => e.id === outgoing[0].id).length !== 1) return null;
  const candidates = graph.nodes.filter((n): n is RepliesNode => n.id === outgoing[0].target && n.type === 'template_replies' && n.config.template_node_id === template.id);
  if (candidates.length !== 1 || (node.type === 'template_replies' && candidates[0].id !== node.id)) return null;
  const replies = candidates[0], incoming = graph.edges.filter(e => e.target === replies.id);
  if (incoming.length !== 1 || incoming[0] !== outgoing[0]) return null;
  return { template, replies };
}

const positionOf = (graph: WorkflowGraphDefinition, id: string): WorkflowPoint => graph.presentation.positions[id] || { x: 0, y: 0 };
const dimensions: Record<WorkflowVisualCard['kind'], WorkflowCardSize> = {
  form_trigger: { width: 256, height: 152 }, message: { width: 296, height: 452 },
  official_template: { width: 296, height: 452 }, template_replies: { width: 296, height: 252 },
  send_media: { width: 264, height: 280 }, clinic_agent: { width: 264, height: 164 },
};
function portLabel(node: WorkflowNode, port: string, graph: WorkflowGraphDefinition, resources?: WorkflowResources): string {
  if (port === 'free_text') return 'Outra resposta';
  if (port === 'expired') return 'Prazo expirado';
  if (port === 'completed') return 'Fim do percurso';
  if (port === 'submitted') return 'Formulário recebido';
  if (port === 'accepted') return 'Após envio confirmado';
  if (node.type === 'template_replies') {
    const template = graph.nodes.find((n): n is TemplateNode => n.id === node.config.template_node_id && n.type === 'official_template');
    const resource = resources?.templates.find(t => t.id === template?.config.template_id);
    return quickReplies(resource).find(b => port === `button:${b.index}`)?.text || 'Resposta rápida';
  }
  return 'Saída indisponível';
}
export function projectWorkflowGraph(graph: WorkflowGraphDefinition, resources?: WorkflowResources, sizes: Record<string, WorkflowCardSize> = {}): WorkflowProjection {
  const cards: WorkflowVisualCard[] = [], hiddenEdgeIds: string[] = [];
  const cardByNodeId: Record<string, WorkflowVisualCard> = Object.create(null);
  graph.nodes.forEach(node => {
    const group = findMessageGroup(graph, node.id);
    if (group && node.id === group.replies.id) return;
    const kind = group ? 'message' : node.type, source = group?.replies || node;
    const minimum = dimensions[kind], supplied = sizes[node.id];
    const width = supplied && Number.isFinite(supplied.width) ? Math.max(minimum.width, supplied.width) : minimum.width;
    const incoming = node.type === 'clinic_agent' ? graph.edges.filter(e => e.target === node.id) : [];
    const hasMediaInput = incoming.some(e => graph.nodes.some(n => n.id === e.source && n.type === 'send_media'));
    const inputHeight = incoming.length ? 66 + Math.max(0, incoming.length - (hasMediaInput ? 2 : 1)) * 52 : 0;
    const height = Math.max(minimum.height, inputHeight, workflowPorts(source).length * 40 + 92,
      supplied && Number.isFinite(supplied.height) ? supplied.height : 0);
    const position = { ...positionOf(graph, node.id) };
    const card: WorkflowVisualCard = { id: node.id, nodeIds: group ? [group.template.id, group.replies.id] : [node.id], node, replyNode: group?.replies, kind, position, width, height, outputs: [] };
    card.outputs = workflowPorts(source).map((port, index, all) => {
      const edge = graph.edges.find(e => e.source === source.id && e.source_port === port);
      const side: WorkflowVisualPort['side'] = node.type === 'send_media' || node.type === 'clinic_agent' ? 'bottom' : 'right';
      const override = supplied?.portY?.[port];
      const y = typeof override === 'number' && Number.isFinite(override) ? Math.min(height - 20, Math.max(20, override)) : height - (all.length - index) * 40 + 20;
      return { id: JSON.stringify([source.id, port]), source: source.id, source_port: port,
        label: portLabel(source, port, graph, resources), target: edge?.target ?? null,
        terminal: isTerminalPort(port), edgeId: edge?.id, side,
        point: side === 'bottom' ? { x: position.x + width / 2, y: position.y + height } : { x: position.x + width, y: position.y + y } };
    });
    cards.push(card);
    card.nodeIds.forEach(id => { cardByNodeId[id] = card; });
    if (group) hiddenEdgeIds.push(graph.edges.find(e => e.source === group.template.id && e.source_port === 'accepted')!.id);
  });
  return { cards, hiddenEdgeIds, cardByNodeId };
}

/** Incoming anchors are view coordinates; every branch retains the same target ID. */
export function getWorkflowInputPoint(projection: WorkflowProjection, targetId: string, edge?: WorkflowEdge): WorkflowPoint | null {
  const card = projection.cardByNodeId[targetId];
  if (!card || card.node.type === 'form_trigger') return null;
  if (card.node.type === 'clinic_agent' && edge) {
    const incoming = projection.cards.flatMap(c => c.outputs).filter(p => p.target === targetId);
    const media = incoming.find(p => projection.cardByNodeId[p.source]?.node.type === 'send_media');
    if (media?.edgeId === edge.id) return { x: card.position.x + card.width / 2, y: card.position.y };
    const lateral = incoming.filter(p => p !== media), index = lateral.findIndex(p => p.edgeId === edge.id);
    return { x: card.position.x, y: card.position.y + 44 + Math.max(0, index) * 52 };
  }
  return { x: card.position.x, y: card.position.y + 44 };
}

function boundedDelta(graph: WorkflowGraphDefinition, ids: string[], delta: WorkflowPoint): WorkflowPoint | null {
  if (!Number.isFinite(delta.x) || !Number.isFinite(delta.y)) return null;
  const points = ids.map(id => positionOf(graph, id));
  return { x: Math.max(-100000 - Math.min(...points.map(p => p.x)), Math.min(100000 - Math.max(...points.map(p => p.x)), delta.x)),
    y: Math.max(-100000 - Math.min(...points.map(p => p.y)), Math.min(100000 - Math.max(...points.map(p => p.y)), delta.y)) };
}
export function moveWorkflowCard(graph: WorkflowGraphDefinition, cardId: string, delta: WorkflowPoint): WorkflowGraphDefinition {
  if (!graph.nodes.some(n => n.id === cardId)) return graph;
  const group = findMessageGroup(graph, cardId), ids = group ? [group.template.id, group.replies.id] : [cardId];
  const safe = boundedDelta(graph, ids, delta);
  if (!safe || (!safe.x && !safe.y)) return graph;
  const positions = { ...graph.presentation.positions };
  ids.forEach(id => { const old = positionOf(graph, id); positions[id] = { x: old.x + safe.x, y: old.y + safe.y }; });
  return { ...graph, presentation: { ...graph.presentation, positions } };
}

export function connectionError(graph: WorkflowGraphDefinition, sourceId: string, port: string, targetId: string | null): string | null {
  const sources = graph.nodes.filter(n => n.id === sourceId), targets = graph.nodes.filter(n => n.id === targetId);
  if (sources.length !== 1) return 'A etapa de origem está indisponível ou repetida.';
  const source = sources[0], existing = graph.edges.filter(e => e.source === sourceId && e.source_port === port);
  if (!workflowPorts(source).includes(port)) return 'Esta saída não pertence à etapa.';
  if (existing.length > 1) return 'Revise as conexões repetidas desta saída.';
  if (isTerminalPort(port)) return targetId === null ? null : 'Esta saída encerra o percurso e não aceita outro destino.';
  if (targetId === null) return null; // Explicit disconnect is an incomplete draft, not termination.
  if (targets.length !== 1) return 'A etapa de destino está indisponível ou repetida.';
  const target = targets[0];
  if (target.type === 'form_trigger') return 'O formulário não pode receber conexões.';
  if (source.type === 'form_trigger' && target.type !== 'official_template') return 'O formulário precisa iniciar com uma mensagem oficial.';
  if (source.type === 'official_template' && target.type !== 'template_replies') return 'Conecte o template à sua espera por respostas.';
  if (source.type === 'template_replies' && port === 'free_text' && target.type !== 'clinic_agent') return 'Outra resposta precisa seguir para o agente principal.';
  if (target.type === 'template_replies') {
    if (source.type !== 'official_template' || target.config.template_node_id !== source.id) return 'A espera precisa receber o template de origem correspondente.';
    if (graph.edges.some(e => e.target === target.id && !(e.source === sourceId && e.source_port === port))) return 'A espera aceita uma única conexão de entrada.';
  }
  const visited = new Set<string>();
  const reachesSource = (id: string): boolean => {
    if (id === sourceId) return true;
    if (visited.has(id)) return false;
    visited.add(id);
    return graph.edges.filter(e => e.source === id && e.target !== null && !(e.source === sourceId && e.source_port === port)).some(e => reachesSource(e.target!));
  };
  return reachesSource(targetId) ? 'Esta conexão criaria um ciclo no fluxo.' : null;
}
function nextEdgeId(graph: WorkflowGraphDefinition, source: string, port: string): string {
  const prefix = `${source}-${port}`.slice(0, 68);
  let candidate = prefix, index = 1;
  while (graph.edges.some(e => e.id === candidate)) candidate = `${prefix}-${index++}`;
  return candidate;
}
export function setWorkflowEdgeTarget(graph: WorkflowGraphDefinition, sourceId: string, sourcePort: string, targetId: string | null): { graph: WorkflowGraphDefinition; error: string | null } {
  const error = connectionError(graph, sourceId, sourcePort, targetId);
  if (error) return { graph, error };
  const index = graph.edges.findIndex(e => e.source === sourceId && e.source_port === sourcePort);
  if (index !== -1 && graph.edges[index].target === targetId) return { graph, error: null };
  const edges = [...graph.edges];
  if (index === -1) edges.push({ id: nextEdgeId(graph, sourceId, sourcePort), source: sourceId, source_port: sourcePort, target: targetId });
  else edges[index] = { ...edges[index], target: targetId };
  return { graph: { ...graph, edges }, error: null };
}
export function removeWorkflowNodes(graph: WorkflowGraphDefinition, nodeIds: string[]): WorkflowGraphDefinition {
  const removed = new Set(nodeIds);
  if (!graph.nodes.some(n => removed.has(n.id))) return graph;
  return { ...graph, nodes: graph.nodes.filter(n => !removed.has(n.id)),
    edges: graph.edges.filter(e => !removed.has(e.source) && (e.target === null || !removed.has(e.target))),
    presentation: { ...graph.presentation, positions: Object.fromEntries(Object.entries(graph.presentation.positions).filter(([id]) => !removed.has(id))) } };
}

export function fitWorkflowViewport(projection: WorkflowProjection, width: number, height: number): WorkflowGraphDefinition['presentation']['viewport'] {
  if (!projection.cards.length || !Number.isFinite(width) || !Number.isFinite(height) || width <= 80 || height <= 80) return { x: 0, y: 0, zoom: 1 };
  const minX = Math.min(...projection.cards.map(c => c.position.x)), minY = Math.min(...projection.cards.map(c => c.position.y));
  const maxX = Math.max(...projection.cards.map(c => c.position.x + c.width)), maxY = Math.max(...projection.cards.map(c => c.position.y + c.height));
  const zoom = Math.max(0.1, Math.min(1, (width - 80) / (maxX - minX), (height - 80) / (maxY - minY)));
  return { x: Math.max(-100000, Math.min(100000, (width - (maxX - minX) * zoom) / 2 - minX * zoom)),
    y: Math.max(-100000, Math.min(100000, (height - (maxY - minY) * zoom) / 2 - minY * zoom)), zoom };
}

/** Explicit layout action changes positions only, preserving canonical array order. */
export function autoLayoutWorkflow(graph: WorkflowGraphDefinition, resources?: WorkflowResources): WorkflowGraphDefinition {
  const projection = projectWorkflowGraph(graph, resources), positions = { ...graph.presentation.positions };
  if (!projection.cards.length) return graph;
  const ranks = new Map(projection.cards.map(c => [c.id, 0]));
  const links = graph.edges.filter(e => e.target !== null && !projection.hiddenEdgeIds.includes(e.id))
    .map(e => [projection.cardByNodeId[e.source]?.id, projection.cardByNodeId[e.target!]?.id]).filter(([a, b]) => a && b && a !== b);
  for (let iteration = 0; iteration < projection.cards.length; iteration++) {
    let changed = false;
    links.forEach(([a, b]) => { const next = (ranks.get(a) || 0) + 1; if (next > (ranks.get(b) || 0)) { ranks.set(b, next); changed = true; } });
    if (!changed) break;
    if (iteration === projection.cards.length - 1) return graph; // Keep an invalid cyclic draft intact.
  }
  const displayRank = (card: WorkflowVisualCard): number => {
    const rank = ranks.get(card.id) || 0;
    if (card.node.type === 'clinic_agent') {
      const media = graph.edges.filter(e => e.target === card.id).map(e => projection.cardByNodeId[e.source]).find(c => c?.node.type === 'send_media');
      if (media) return ranks.get(media.id) || 0;
    }
    return rank;
  };
  const rows = new Map<number, number>();
  const visualOrder = [...projection.cards].sort((a, b) => displayRank(a) - displayRank(b) || (ranks.get(a.id) || 0) - (ranks.get(b.id) || 0));
  visualOrder.forEach(card => {
    const rank = displayRank(card);
    const row = rows.get(rank) ?? (card.node.type === 'form_trigger' ? 320 : card.node.type === 'send_media' ? 336 : 260);
    const desired = { x: rank === 0 ? 48 : rank === 1 ? 416 : 804 + (rank - 2) * 388, y: row };
    const moved = moveWorkflowCard({ ...graph, presentation: { ...graph.presentation, positions } }, card.id, { x: desired.x - card.position.x, y: desired.y - card.position.y });
    Object.assign(positions, moved.presentation.positions);
    rows.set(rank, row + card.height + (card.node.type === 'send_media' ? 132 : 72));
  });
  return { ...graph, presentation: { ...graph.presentation, positions } };
}
